import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { TrendingUp, GitFork, Star, ShieldCheck, Sparkles, Loader2 } from "lucide-react";

/**
 * /trending — public leaderboard of composed models by viral score.
 * score = (stars + forks) / age_days^0.6 · (1.2 if grade >= 80 else 1.0)
 */
export default function Trending() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/ai-models-trending?limit=24`)
      .then(({ data }) => setModels(data.models || []))
      .catch((e) => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 space-y-6">
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-3">
          <TrendingUp size={12} strokeWidth={2.5} /> Trending
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold leading-tight" data-testid="trending-title">
          What the crowd is <span className="bg-amber-200 px-2 border-2 border-slate-900 inline-block rotate-[-1deg]">forking</span> right now
        </h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          Public composed models ranked by viral velocity — <span className="font-mono text-xs">(stars + forks) / age<sup>0.6</sup></span> with a 20% boost for grade ≥ 80.
          Fresh models beat stale ones with the same star count.
        </p>
      </div>

      {err && <div className="brut-card p-4 text-red-600 text-sm">{err}</div>}

      {loading ? (
        <div className="brut-card p-8 flex items-center justify-center gap-2 text-slate-500">
          <Loader2 size={16} className="animate-spin" /> Loading trending models…
        </div>
      ) : models.length === 0 ? (
        <div className="brut-card p-8 text-center">
          <div className="text-4xl mb-2">🌱</div>
          <div className="font-heading font-bold text-lg mb-1">No trending models yet</div>
          <p className="text-sm text-slate-600 mb-4">Be the first to publish. Models shared publicly appear here.</p>
          <Link to="/studio/new/composed" className="brut-btn text-sm" data-testid="trending-empty-cta">
            <Sparkles size={12} strokeWidth={3} /> Compose one
          </Link>
        </div>
      ) : (
        <ol className="space-y-3" data-testid="trending-list">
          {models.map((m, idx) => <TrendingRow key={m.ai_model_id} m={m} rank={idx + 1} />)}
        </ol>
      )}
    </div>
  );
}

function TrendingRow({ m, rank }) {
  const grade = m.grade_overall ?? m.last_grade?.average?.overall;
  const stars = m.star_count || 0;
  const forks = m.fork_count || 0;
  const rankColor = rank <= 3 ? "#EA580C" : rank <= 10 ? "#F59E0B" : "#94A3B8";

  return (
    <li className="brut-card p-4 flex items-center gap-4 hover:-translate-y-0.5 transition-transform" data-testid={`trending-row-${rank}`}>
      <div
        className="w-12 h-12 rounded-xl border-2 border-slate-900 flex items-center justify-center font-heading font-bold text-lg shrink-0"
        style={{ backgroundColor: rankColor + "22", color: rankColor }}
      >
        #{rank}
      </div>
      <div className="w-12 h-12 rounded-xl border-2 border-slate-900 bg-amber-100 flex items-center justify-center text-2xl shrink-0">
        {m.avatar || "🤖"}
      </div>
      <div className="flex-1 min-w-0">
        <Link to={`/studio/${m.ai_model_id}`} className="block hover:text-amber-700">
          <div className="font-heading font-bold text-lg leading-tight truncate">{m.name}</div>
        </Link>
        <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-2 flex-wrap">
          <span>by <b>{m.author || "creator"}</b></span>
          {m.age_days !== undefined && <span className="text-slate-400">·</span>}
          {m.age_days !== undefined && <span>{m.age_days < 1 ? "<1 day" : `${Math.round(m.age_days)}d`} old</span>}
          <span className="text-slate-400">·</span>
          <span className="font-mono text-slate-500 truncate">{m.base_brain_id}</span>
        </div>
        {m.description && <p className="text-xs text-slate-500 line-clamp-1 mt-1">{m.description}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
        {grade !== undefined && grade !== null && (
          <span
            className="px-2 py-0.5 rounded-full font-heading font-bold uppercase tracking-widest inline-flex items-center gap-1 text-[10px]"
            style={{ backgroundColor: gradeColor(grade) + "22", color: gradeColor(grade), border: `1.5px solid ${gradeColor(grade)}` }}
          >
            <ShieldCheck size={9} strokeWidth={3} /> {grade}
          </span>
        )}
        <span className="text-xs font-mono text-slate-600 inline-flex items-center gap-1">
          <Star size={11} strokeWidth={2.5} /> {stars}
        </span>
        <span className="text-xs font-mono text-slate-600 inline-flex items-center gap-1">
          <GitFork size={11} strokeWidth={2.5} /> {forks}
        </span>
        <div className="hidden sm:block text-right">
          <div className="text-[9px] uppercase tracking-widest text-slate-400 font-heading font-bold">Score</div>
          <div className="text-sm font-heading font-bold text-slate-900 tabular-nums">{(m.trending_score ?? 0).toFixed(2)}</div>
        </div>
      </div>
    </li>
  );
}

function gradeColor(v) {
  if (v >= 80) return "#10B981";
  if (v >= 50) return "#F59E0B";
  return "#EF4444";
}
